// frontend/src/pages/Partner/PartnerCurriculum.jsx

import {

  useCallback,

  useEffect,

  useMemo,

  useState,

} from "react";



import {

  ArrowLeft,

  ArrowRight,

  Award,

  BookOpen,

  CheckCircle2,

  ChevronRight,

  CircleHelp,

  Clock3,

  ExternalLink,

  FileDown,

  FileText,

  FolderOpen,

  Layers3,

  Lightbulb,

  ListChecks,

  Quote,

  RefreshCw,

  Search,

} from "lucide-react";



import {

  Link,

  useSearchParams,

} from "react-router-dom";



import {

  fetchPartnerCurriculumCatalog,

  fetchPartnerCurriculumLevel,

} from "../../lib/partner/partnerCurriculum";



const RESULT_STEP =

  "resultStep";



const WELCOME_STEP =

  "welcomeStep";



const SELECT_STEPS = new Set([

  "scenarioSelectStep",

  "quizSelectStep",

]);



const DRAG_STEPS = new Set([

  "scenarioDragStep",

  "quizDragStep",

]);



const SCENARIO_STEPS =

  new Set([

    "scenarioSelectStep",

    "scenarioDragStep",

  ]);



function cleanText(value) {

  return String(

    value ?? ""

  ).trim();

}



function numberValue(value) {

  const parsed =

    Number(value);



  return Number.isFinite(

    parsed

  )

    ? parsed

    : 0;

}



function textList(value) {

  if (Array.isArray(value)) {

    return value

      .map(cleanText)

      .filter(Boolean);

  }



  const cleanValue =

    cleanText(value);



  if (!cleanValue) {

    return [];

  }



  return cleanValue

    .split(/\r?\n+/)

    .map(cleanText)

    .filter(Boolean);

}



function matchesSearch(

  searchTerm,

  ...values

) {

  const query = cleanText(

    searchTerm

  ).toLowerCase();



  if (!query) {

    return true;

  }



  return values.some(

    (value) =>

      cleanText(value)

        .toLowerCase()

        .includes(query)

  );

}



function getOptionText(

  option

) {

  return (

    cleanText(

      option?.title

    ) ||

    cleanText(

      option?.text

    ) ||

    cleanText(

      option?.label

    ) ||

    cleanText(

      option?.answer

    ) ||

    "Answer option"

  );

}



function isCorrectOption(

  option

) {

  return (

    option?.isCorrect ===

      true ||

    option?.correct ===

      true ||

    cleanText(

      option?.status

    ).toLowerCase() ===

      "correct"

  );

}



function getDragPairs(

  step

) {

  const directPairs =

    Array.isArray(

      step?.pairs

    )

      ? step.pairs

      : [];



  if (

    directPairs.length > 0

  ) {

    return directPairs

      .map(

        (

          pair,

          index

        ) => ({

          id:

            pair?._key ||

            pair?.id ||

            `pair-${index}`,



          left:

            cleanText(

              pair?.dragText

            ) ||

            cleanText(

              pair?.leftText

            ) ||

            cleanText(

              pair?.word

            ) ||

            cleanText(

              pair?.text

            ),



          right:

            cleanText(

              pair?.dropText

            ) ||

            cleanText(

              pair?.rightText

            ) ||

            cleanText(

              pair?.definition

            ) ||

            cleanText(

              pair?.targetText

            ),

        })

      )

      .filter(

        (pair) =>

          pair.left ||

          pair.right

      );

  }



  const items =

    Array.isArray(

      step?.items

    )

      ? step.items

      : [];



  const targets =

    Array.isArray(

      step?.targets

    )

      ? step.targets

      : [];



  return items

    .map(

      (

        item,

        index

      ) => {

        const matchKey =

          cleanText(

            item?.matchKey

          );



        const target =

          targets.find(

            (

              candidate

            ) =>

              cleanText(

                candidate

                  ?.matchKey

              ) ===

              matchKey

          );



        return {

          id:

            item?._key ||

            item?.id ||

            `item-${index}`,



          left:

            cleanText(

              item?.text

            ) ||

            cleanText(

              item?.title

            ) ||

            cleanText(

              item?.dragText

            ),



          right:

            cleanText(

              target?.text

            ) ||

            cleanText(

              target?.title

            ) ||

            cleanText(

              target?.dropText

            ),

        };

      }

    )

    .filter(

      (pair) =>

        pair.left ||

        pair.right

    );

}



function getIllustrationUrl(

  step

) {

  return (

    cleanText(

      step?.illustration

        ?.asset?.url

    ) ||

    cleanText(

      step?.image

        ?.asset?.url

    ) ||

    ""

  );

}



function buildDisplayBlocks(

  steps

) {

  const blocks = [];



  (

    Array.isArray(steps)

      ? steps

      : []

  ).forEach(

    (

      sourceStep,

      index

    ) => {

      if (!sourceStep) {

        return;

      }



      const step = {

        ...sourceStep,

        __index: index,

      };



      if (

        step._type ===

          WELCOME_STEP ||

        step._type ===

          RESULT_STEP

      ) {

        return;

      }



      if (

        SCENARIO_STEPS.has(

          step._type

        ) &&

        blocks.length > 0 &&

        blocks[

          blocks.length - 1

        ].step?._type ===

          "conceptExampleStep"

      ) {

        blocks[

          blocks.length - 1

        ].scenarios.push(

          step

        );



        return;

      }



      blocks.push({

        step,

        scenarios: [],

      });

    }

  );



  return blocks;

}



function filterCatalog(

  catalog,

  searchTerm

) {

  const query =

    cleanText(searchTerm);



  if (!query) {

    return catalog;

  }



  return catalog.reduce(

    (

      questRows,

      quest

    ) => {

      const questMatches =

        matchesSearch(

          query,

          quest.id,

          quest.title,

          `Quest ${quest.id}`

        );



      const subquests =

        quest.subquests.reduce(

          (

            subquestRows,

            subquest

          ) => {

            const subquestMatches =

              matchesSearch(

                query,

                subquest.id,

                subquest.title,

                `Subquest ${subquest.id}`

              );



            const levels =

              questMatches ||

              subquestMatches

                ? subquest.levels

                : subquest.levels.filter(

                    (

                      level

                    ) =>

                      matchesSearch(

                        query,

                        level.title,

                        level.level_key,

                        `Level ${level.level_key}`

                      )

                  );



            if (

              levels.length >

              0

            ) {

              subquestRows.push(

                {

                  ...subquest,

                  levels,

                }

              );

            }



            return subquestRows;

          },

          []

        );



      if (

        subquests.length > 0

      ) {

        questRows.push({

          ...quest,

          subquests,

        });

      }



      return questRows;

    },

    []

  );

}



export default function PartnerCurriculum() {

  const [

    searchParams,

  ] = useSearchParams();



  const selectedCohortId =

    searchParams.get(

        "cohort"

    ) || "";



  const selectedQuestId =

    searchParams.get(

      "quest"

    ) || "";



  const selectedSubquestId =

    searchParams.get(

      "subquest"

    ) || "";



  const selectedLevelKey =

    searchParams.get(

      "level"

    ) || "";



  const hasSelectedLevel =

    Boolean(

      selectedQuestId &&

        selectedSubquestId &&

        selectedLevelKey

    );



  const [

    catalog,

    setCatalog,

  ] = useState([]);



  const [

    level,

    setLevel,

  ] = useState(null);



  const [

    search,

    setSearch,

  ] = useState("");



  const [

    loadingCatalog,

    setLoadingCatalog,

  ] = useState(true);



  const [

    loadingLevel,

    setLoadingLevel,

  ] = useState(false);



  const [

    error,

    setError,

  ] = useState("");



  const loadCatalog =

    useCallback(

      async () => {

        try {

          setLoadingCatalog(

            true

          );



          setError("");



          const rows =

            await fetchPartnerCurriculumCatalog({
              reviewAll: true,
            });



          setCatalog(rows);

        } catch (

          loadError

        ) {

          console.error(

            "Partner curriculum catalog loading failed:",

            loadError

          );



          setError(

            loadError?.message ||

              "The curriculum could not be loaded."

          );

        } finally {

          setLoadingCatalog(

            false

          );

        }

            },
      []
    );



  const loadSelectedLevel =

    useCallback(

      async () => {

        if (

          !hasSelectedLevel

        ) {

          setLevel(null);

          return;

        }



        try {

          setLoadingLevel(

            true

          );



          setError("");



          const row =

            await fetchPartnerCurriculumLevel({

                questId:

                    selectedQuestId,



                subquestId:

                    selectedSubquestId,



                levelKey:

                    selectedLevelKey,



                reviewAll: true,
                });



          if (!row) {

            throw new Error(

              "This curriculum level could not be found."

            );

          }



          setLevel(row);

        } catch (

          loadError

        ) {

          console.error(

            "Partner curriculum level loading failed:",

            loadError

          );



          setLevel(null);



          setError(

            loadError?.message ||

              "The curriculum level could not be loaded."

          );

        } finally {

          setLoadingLevel(

            false

          );

        }

      },

      [
        hasSelectedLevel,
        selectedLevelKey,

        selectedQuestId,

        selectedSubquestId,

      ]

    );



  useEffect(() => {

    if (

      !hasSelectedLevel

    ) {

      loadCatalog();

    }

  }, [

    hasSelectedLevel,

    loadCatalog,

  ]);



  useEffect(() => {

    loadSelectedLevel();

  }, [loadSelectedLevel]);



    if (hasSelectedLevel) {

    return (

      <PartnerCurriculumLevelView

        level={level}

        loading={

          loadingLevel

        }

        error={error}

        onRetry={

          loadSelectedLevel

        }

        cohortId={

          selectedCohortId

        }

      />

    );

  }



  return (

    <PartnerCurriculumCatalog

      catalog={catalog}

      loading={

        loadingCatalog

      }

      error={error}

      search={search}

      onSearchChange={

        setSearch

      }

      onRetry={

        loadCatalog

      }

      cohortId={

        selectedCohortId

      }

    />

  );

}



function PartnerCurriculumCatalog({

  catalog,

  loading,

  error,

  search,

  onSearchChange,

  onRetry,

  cohortId,

}) {

  const filteredCatalog =

    useMemo(

      () =>

        filterCatalog(

          catalog,

          search

        ),

      [catalog, search]

    );



  const totals =

    useMemo(() => {

      return catalog.reduce(

        (

          summary,

          quest

        ) => {

          summary.quests +=

            1;



          summary.subquests +=

            quest.subquests

              .length;



          summary.levels +=

            quest.subquests.reduce(

              (

                count,

                subquest

              ) =>

                count +

                subquest

                  .levels

                  .length,

              0

            );



          return summary;

        },

        {

          quests: 0,

          subquests: 0,

          levels: 0,

        }

      );

    }, [catalog]);



  return (

    <>

      <header className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">

        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">

          <div>

            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

              Partner ·

              Curriculum

            </p>



            <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] md:text-[42px]">

              Gwin curriculum

            </h1>



            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#757575] md:text-base">

              Review the

              published learning

              content, examples,

              assessments and

              practical work

              learners complete

              in Gwin.

            </p>

          </div>



          <div className="grid grid-cols-3 gap-3">

            <CatalogStat

              label="Quests"

              value={

                totals.quests

              }

            />



            <CatalogStat

              label="Subquests"

              value={

                totals.subquests

              }

            />



            <CatalogStat

              label="Levels"

              value={

                totals.levels

              }

            />

          </div>

        </div>

      </header>



      <section className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-5 shadow-sm md:p-6">

        <label

          htmlFor="partner-curriculum-search"

          className="text-xs font-black uppercase tracking-wider text-[#757575]"

        >

          Search curriculum

        </label>



        <div className="relative mt-2">

          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#A0A0A0]" />



          <input

            id="partner-curriculum-search"

            type="search"

            value={search}

            onChange={(

              event

            ) =>

              onSearchChange(

                event.target

                  .value

              )

            }

            placeholder="Search by quest, skill, subquest or level"

            className="w-full rounded-2xl border border-[#DDDDDD] bg-white py-3 pl-12 pr-4 text-sm font-semibold outline-none transition focus:border-[#FF914D] focus:ring-4 focus:ring-[#FFF0E6]"

          />

        </div>

      </section>



      {error && (

        <ErrorBlock

          message={error}

          onRetry={onRetry}

        />

      )}



            {loading ? (

        <LoadingBlock message="Loading curriculum…" />

      ) : filteredCatalog.length ===

        0 ? (

        <EmptyBlock

          message={

            search

              ? "No curriculum levels match your search."

              : "No published curriculum is currently available."

          }

        />

      ) : (

        <div className="mt-6 space-y-6">

          {filteredCatalog.map(

            (quest) => (

              <QuestCurriculumCard

                key={

                  quest.id

                }

                quest={

                  quest

                }

                cohortId={

                  cohortId

                }

              />

            )

          )}

        </div>

      )}

    </>

  );

}



function QuestCurriculumCard({

  quest,

  cohortId,

}) {

  const levelCount =

    quest.subquests.reduce(

      (

        count,

        subquest

      ) =>

        count +

        subquest.levels

          .length,

      0

    );



  return (

    <section className="overflow-hidden rounded-[26px] border border-[#EFEFEF] bg-white shadow-sm">

      <header className="border-b border-[#EFEFEF] bg-[#FFF9F5] p-5 md:p-6">

        <div className="flex items-start gap-4">

          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-[#FF914D] text-white">

            <BookOpen className="h-6 w-6" />

          </div>



          <div className="min-w-0">

            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

              Quest{" "}

              {quest.id}

            </p>



            <h2 className="mt-1 text-2xl font-black tracking-[-0.02em]">

              {quest.title}

            </h2>



            <p className="mt-2 text-sm font-semibold text-[#757575]">

              {

                quest

                  .subquests

                  .length

              }{" "}

              subquests ·{" "}

              {levelCount}{" "}

              levels

            </p>

                        {Array.isArray(

              quest.assigned_cohorts

            ) &&

              quest

                .assigned_cohorts

                .length > 0 && (

                <div className="mt-3 flex flex-wrap gap-2">

                  {quest.assigned_cohorts.map(

                    (

                      assignedCohort

                    ) => (

                      <span

                        key={

                          assignedCohort.id

                        }

                        className="rounded-full border border-[#FFD9BF] bg-white px-3 py-1 text-xs font-black text-[#75523B]"

                      >

                        {

                          assignedCohort.name

                        }

                      </span>

                    )

                  )}

                </div>

              )}

          </div>

        </div>

      </header>



      <div className="space-y-6 p-5 md:p-6">

        {quest.subquests.map(

          (subquest) => (

            <div

              key={

                subquest.id

              }

            >

              <div className="flex items-center gap-3">

                <Layers3 className="h-5 w-5 text-[#FF914D]" />



                <div>

                  <p className="text-xs font-black uppercase tracking-wider text-[#757575]">

                    Subquest{" "}

                    {

                      subquest.id

                    }

                  </p>



                  <h3 className="mt-1 text-lg font-black">

                    {

                      subquest.title

                    }

                  </h3>

                </div>

              </div>



              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">

                {subquest.levels.map(

                  (

                    level

                  ) => (

                    <Link

                      key={

                        level.id

                      }

                      to={`/partner/curriculum?quest=${encodeURIComponent(

                        quest.id

                      )}&subquest=${encodeURIComponent(

                        subquest.id

                      )}&level=${encodeURIComponent(

                        level.level_key

                      )}`+

                        (

                            cohortId

                                ? `&cohort=${encodeURIComponent(

                                    cohortId

                                    )}`

                                : ""

                        )

                    }

                      className="group rounded-[20px] border border-[#EFEFEF] bg-white p-4 transition hover:-translate-y-0.5 hover:border-[#FFB17D] hover:shadow-md"

                    >

                      <div className="flex items-start justify-between gap-3">

                        <div className="min-w-0">

                          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

                            Level{" "}

                            {

                              level.level_key

                            }

                          </p>



                          <h4 className="mt-2 break-words font-black leading-snug text-[#333333]">

                            {

                              level.title

                            }

                          </h4>

                        </div>



                        <ChevronRight className="h-5 w-5 flex-none text-[#A0A0A0] transition group-hover:translate-x-0.5 group-hover:text-[#FF914D]" />

                      </div>



                      <div className="mt-4 flex flex-wrap gap-2 text-xs font-bold text-[#757575]">

                        {level.xp_max >

                          0 && (

                          <span className="rounded-full bg-[#FFF4EC] px-3 py-1">

                            {

                              level.xp_max

                            }{" "}

                            XP

                          </span>

                        )}



                        {level.max_questions >

                          0 && (

                          <span className="rounded-full bg-[#F4F4F4] px-3 py-1">

                            {

                              level.max_questions

                            }{" "}

                            questions

                          </span>

                        )}

                      </div>

                    </Link>

                  )

                )}

              </div>

            </div>

          )

        )}

      </div>

    </section>

  );

}



function PartnerCurriculumLevelView({

  level,

  loading,

  error,

  onRetry,

  cohortId,

}) {

  const welcome =

    useMemo(

      () =>

        level?.steps?.find(

          (step) =>

            step?._type ===

            WELCOME_STEP

        ) || null,

      [level]

    );



  const displayBlocks =

    useMemo(

      () =>

        buildDisplayBlocks(

          level?.steps

        ),

      [level]

    );



  const assessmentCount =

    useMemo(

      () =>

        (

          level?.steps ||

          []

        ).filter(

          (step) =>

            SELECT_STEPS.has(

              step?._type

            ) ||

            DRAG_STEPS.has(

              step?._type

            )

        ).length,

      [level]

    );



  if (loading) {

    return (

      <>

        <BackToCurriculum

            cohortId={

            cohortId

            }

        />



        <LoadingBlock message="Loading the curriculum level…" />

      </>

    );

  }



  if (

    error ||

    !level

  ) {

    return (

      <>

        <BackToCurriculum

            cohortId={

                cohortId

            }

        />



        <ErrorBlock

          message={

            error ||

            "The curriculum level could not be found."

          }

          onRetry={onRetry}

        />

      </>

    );

  }



  const levelTitle =

    cleanText(

      welcome?.heading

    ) ||

    cleanText(

      level.title

    ) ||

    `Level ${level.level_key}`;



  const learningOutcomes =

    textList(

      welcome

        ?.learningOutcomes

    );



  return (

    <>

      <BackToCurriculum

        cohortId={

            cohortId

        }

    />



      <header className="mt-6 overflow-hidden rounded-[28px] border border-[#EFEFEF] bg-white shadow-sm">

        <div className="bg-[#FFF9F5] p-6 md:p-8">

          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

            Quest{" "}

            {level.quest_id} ·{" "}

            {

              level.quest_title

            }

          </p>



          <p className="mt-2 text-sm font-bold text-[#757575]">

            {

              level.subquest_title

            }{" "}

            · Level{" "}

            {

              level.level_key

            }

          </p>



          <h1 className="mt-3 max-w-4xl text-[34px] font-black tracking-[-0.03em] md:text-[44px]">

            {levelTitle}

          </h1>



          {cleanText(

            welcome?.introText

          ) && (

            <p className="mt-4 max-w-4xl text-sm leading-relaxed text-[#606060] md:text-base">

              {

                welcome.introText

              }

            </p>

          )}



          <div className="mt-6 flex flex-wrap gap-3">

            <LevelMetaPill

              icon={Clock3}

              label={

                cleanText(

                  welcome?.duration

                ) ||

                "Duration not supplied"

              }

            />



            <LevelMetaPill

              icon={Award}

              label={`${numberValue(

                welcome?.xpMax ||

                  level.xpMax

              )} XP available`}

            />



            <LevelMetaPill

              icon={

                CircleHelp

              }

              label={`${assessmentCount} assessment${

                assessmentCount ===

                1

                  ? ""

                  : "s"

              }`}

            />

          </div>

        </div>



        {learningOutcomes.length >

          0 && (

          <div className="border-t border-[#EFEFEF] p-6 md:p-8">

            <h2 className="text-lg font-black">

              Learning

              outcomes

            </h2>



            <ul className="mt-4 grid gap-3 md:grid-cols-2">

              {learningOutcomes.map(

                (

                  outcome,

                  index

                ) => (

                  <li

                    key={`${outcome}-${index}`}

                    className="flex items-start gap-3 rounded-2xl bg-[#FAFAFA] p-4 text-sm leading-relaxed"

                  >

                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-[#FF914D]" />



                    <span>

                      {

                        outcome

                      }

                    </span>

                  </li>

                )

              )}

            </ul>

          </div>

        )}

      </header>



            <div className="mt-6 space-y-5">

        {displayBlocks.length ===

        0 ? (

          <EmptyBlock message="No partner-viewable curriculum cards are available for this level." />

        ) : (

          displayBlocks.map(

            (

              block,

              index

            ) => (

              <CurriculumStepCard

                key={

                  block.step

                    ?._key ||

                  `${block.step?._type}-${block.step?.__index}`

                }

                step={

                  block.step

                }

                scenarios={

                  block.scenarios

                }

                cardNumber={

                  index + 1

                }

              />

            )

          )

        )}

      </div>



      <ResourceSection

        resources={

          level.resources

        }

      />

    </>

  );

}



function CurriculumStepCard({

  step,

  scenarios,

  cardNumber,

}) {

  switch (

    step?._type

  ) {

    case "introductionStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Introduction`}

          title="Introduction"

          icon={BookOpen}

        >

          <ParagraphList

            paragraphs={

              step.paragraphs

            }

          />



          <StepIllustration

            step={step}

          />

        </ContentCard>

      );



    case "keyConceptStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · ${

            cleanText(

              step.internalTitle

            ) ||

            "Key concept"

          }`}

          title={

            cleanText(

              step.displayTitle

            ) ||

            cleanText(

              step.internalTitle

            ) ||

            "Key concept"

          }

          icon={Lightbulb}

          tone="concept"

        >

          <ParagraphList

            paragraphs={

              step.paragraphs

            }

          />



          <StepIllustration

            step={step}

          />

        </ContentCard>

      );



    case "conceptExampleStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Example`}

          title={

            cleanText(

              step.displayTitle

            ) ||

            cleanText(

              step.internalTitle

            ) ||

            "Business example"

          }

          icon={Quote}

          tone="example"

        >

          <ParagraphList

            paragraphs={

              step.storyParagraphs

            }

          />



          <StepIllustration

            step={step}

          />



          {scenarios.length >

            0 && (

            <div className="mt-6 space-y-4 border-t border-[#EFEFEF] pt-6">

              {scenarios.map(

                (

                  scenario

                ) => (

                  <AssessmentPanel

                    key={

                      scenario

                        ?._key ||

                      `scenario-${scenario?.__index}`

                    }

                    step={

                      scenario

                    }

                    label="Knowledge check"

                  />

                )

              )}

            </div>

          )}

        </ContentCard>

      );



    case "scenarioSelectStep":

    case "scenarioDragStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Knowledge check`}

          title={

            cleanText(

              step.displayTitle

            ) ||

            "Scenario knowledge check"

          }

          icon={CircleHelp}

          tone="assessment"

        >

          <AssessmentPanel

            step={step}

            label="Answer key"

          />

        </ContentCard>

      );



    case "keyTakeawayStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Summary`}

          title="Key takeaway"

          icon={Lightbulb}

          tone="takeaway"

        >

          <ParagraphList

            paragraphs={

              step.speechText

            }

          />

        </ContentCard>

      );



    case "quizSelectStep":

    case "quizDragStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Final assessment`}

          title={

            cleanText(

              step.displayTitle

            ) ||

            cleanText(

              step.internalTitle

            ) ||

            "Assessment question"

          }

          icon={CircleHelp}

          tone="assessment"

        >

          <AssessmentPanel

            step={step}

            label="Answer key"

          />

        </ContentCard>

      );



    case "practicalStep":

      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Practical assignment`}

          title={

            cleanText(

              step.displayTitle

            ) ||

            "Practical assignment"

          }

          icon={ListChecks}

          tone="practical"

        >

          <div className="whitespace-pre-line text-sm leading-7 text-[#555555] md:text-base">

            {cleanText(

              step.content

            ) ||

              "No practical instructions were supplied."}

          </div>

        </ContentCard>

      );



    default: {

      const paragraphs = [

        ...textList(

          step?.paragraphs

        ),



        ...textList(

          step?.storyParagraphs

        ),



        ...textList(

          step?.speechText

        ),



        ...textList(

          step?.content

        ),

      ];



      if (

        paragraphs.length ===

        0

      ) {

        return null;

      }



      return (

        <ContentCard

          eyebrow={`Card ${cardNumber} · Curriculum content`}

          title={

            cleanText(

              step.displayTitle

            ) ||

            cleanText(

              step.internalTitle

            ) ||

            "Curriculum content"

          }

          icon={FileText}

        >

          <ParagraphList

            paragraphs={

              paragraphs

            }

          />



          <StepIllustration

            step={step}

          />

        </ContentCard>

      );

    }

  }

}



function AssessmentPanel({

  step,

  label,

}) {

  const prompt =

    cleanText(

      step?.prompt

    );



  if (

    SELECT_STEPS.has(

      step?._type

    )

  ) {

    const options =

      Array.isArray(

        step?.options

      )

        ? step.options

        : [];



    return (

      <div className="rounded-[20px] border border-[#E8E8E8] bg-white p-5">

        <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

          {label}

        </p>



        <p className="mt-3 font-black leading-relaxed text-[#333333]">

          {prompt ||

            "Question not supplied"}

        </p>



        <div className="mt-4 space-y-2">

          {options.length ===

          0 ? (

            <p className="text-sm text-[#757575]">

              No answer

              options were

              supplied.

            </p>

          ) : (

            options.map(

              (

                option,

                index

              ) => {

                const correct =

                  isCorrectOption(

                    option

                  );



                return (

                  <div

                    key={

                      option

                        ?._key ||

                      option?.id ||

                      `option-${index}`

                    }

                    className={[

                      "flex items-start gap-3 rounded-2xl border p-4 text-sm",



                      correct

                        ? "border-green-200 bg-green-50 text-green-900"

                        : "border-[#EFEFEF] bg-[#FAFAFA] text-[#555555]",

                    ].join(

                      " "

                    )}

                  >

                    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-white text-xs font-black shadow-sm">

                      {String.fromCharCode(

                        65 +

                          index

                      )}

                    </span>



                    <span className="flex-1 font-semibold leading-relaxed">

                      {getOptionText(

                        option

                      )}

                    </span>



                    {correct && (

                      <span className="inline-flex flex-none items-center gap-1 text-xs font-black">

                        <CheckCircle2 className="h-4 w-4" />



                        Correct

                      </span>

                    )}

                  </div>

                );

              }

            )

          )}

        </div>

      </div>

    );

  }



  const pairs =

    getDragPairs(step);



  return (

    <div className="rounded-[20px] border border-[#E8E8E8] bg-white p-5">

      <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

        {label}

      </p>



      <p className="mt-3 font-black leading-relaxed text-[#333333]">

        {prompt ||

          "Match the related items."}

      </p>



      <div className="mt-4 space-y-3">

        {pairs.length ===

        0 ? (

          <p className="text-sm text-[#757575]">

            No matching pairs

            were supplied.

          </p>

        ) : (

          pairs.map(

            (pair) => (

              <div

                key={

                  pair.id

                }

                className="grid gap-3 rounded-2xl border border-[#EFEFEF] bg-[#FAFAFA] p-4 text-sm md:grid-cols-[1fr_auto_1fr] md:items-center"

              >

                <span className="font-bold text-[#333333]">

                  {pair.left ||

                    "Not supplied"}

                </span>



                <ArrowRight className="h-4 w-4 text-[#FF914D]" />



                <span className="font-bold text-[#333333]">

                  {pair.right ||

                    "Not supplied"}

                </span>

              </div>

            )

          )

        )}

      </div>

    </div>

  );

}



function ContentCard({

  eyebrow,

  title,

  icon: Icon,

  tone = "default",

  children,

}) {

  const toneClasses = {

    default:

      "border-[#EFEFEF] bg-white",



    concept:

      "border-amber-200 bg-[#FFFDF8]",



    example:

      "border-blue-100 bg-[#FBFDFF]",



    assessment:

      "border-purple-100 bg-[#FDFBFF]",



    takeaway:

      "border-orange-200 bg-[#FFF9F5]",



    practical:

      "border-green-200 bg-[#FBFFFC]",

  };



  return (

    <article

      className={`rounded-[26px] border p-5 shadow-sm md:p-7 ${

        toneClasses[

          tone

        ] ||

        toneClasses.default

      }`}

    >

      <header className="flex items-start gap-4">

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF0E6] text-[#FF914D]">

          <Icon className="h-5 w-5" />

        </div>



        <div className="min-w-0">

          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

            {eyebrow}

          </p>



          <h2 className="mt-1 break-words text-xl font-black leading-snug md:text-2xl">

            {title}

          </h2>

        </div>

      </header>



      <div className="mt-5">

        {children}

      </div>

    </article>

  );

}



function ParagraphList({

  paragraphs,

}) {

  const rows =

    textList(paragraphs);



  if (

    rows.length === 0

  ) {

    return (

      <p className="text-sm text-[#757575]">

        No text was supplied

        for this curriculum

        card.

      </p>

    );

  }



  return (

    <div className="space-y-3 text-sm leading-7 text-[#555555] md:text-base">

      {rows.map(

        (

          paragraph,

          index

        ) => (

          <p

            key={`${paragraph}-${index}`}

          >

            {paragraph}

          </p>

        )

      )}

    </div>

  );

}



function StepIllustration({

  step,

}) {

  const imageUrl =

    getIllustrationUrl(

      step

    );



  if (!imageUrl) {

    return null;

  }



  return (

    <div className="mt-6 overflow-hidden rounded-[20px] border border-[#EFEFEF] bg-white">

      <img

        src={imageUrl}

        alt={

          cleanText(

            step?.imageAlt

          ) ||

          cleanText(

            step?.displayTitle

          ) ||

          "Curriculum illustration"

        }

        className="max-h-[420px] w-full object-contain"

        loading="lazy"

      />

    </div>

  );

}



function formatFileSize(

  value

) {

  const bytes =

    Number(value || 0);



  if (

    !Number.isFinite(

      bytes

    ) ||

    bytes <= 0

  ) {

    return "";

  }



  if (bytes < 1024) {

    return `${bytes} B`;

  }



  if (

    bytes <

    1024 * 1024

  ) {

    return `${(

      bytes / 1024

    ).toFixed(1)} KB`;

  }



  return `${(

    bytes /

    (1024 * 1024)

  ).toFixed(1)} MB`;

}



function ResourceSection({

  resources,

}) {

  const rows =

    (

      Array.isArray(

        resources

      )

        ? resources

        : []

    ).filter(

      (resource) =>

        resource?.url

    );



  if (

    rows.length === 0

  ) {

    return null;

  }



  return (

    <section className="mt-6 rounded-[26px] border border-[#EFEFEF] bg-white p-5 shadow-sm md:p-7">

      <header className="flex items-start gap-4">

        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF0E6] text-[#FF914D]">

          <FolderOpen className="h-5 w-5" />

        </div>



        <div>

          <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">

            Level resources

          </p>



          <h2 className="mt-1 text-xl font-black md:text-2xl">

            Downloads and links

          </h2>



          <p className="mt-2 text-sm leading-relaxed text-[#757575]">

            Supporting resources attached to this curriculum level.

          </p>

        </div>

      </header>



      <div className="mt-5 grid gap-3 md:grid-cols-2">

        {rows.map(

          (resource) => {

            const isFile =

              resource.resource_type ===

                "file" ||

              Boolean(

                resource.file_url

              );



            const sizeLabel =

              formatFileSize(

                resource.file_size

              );



            return (

              <a

                key={

                  resource.id

                }

                href={

                  resource.url

                }

                target="_blank"

                rel="noreferrer"

                className="group flex items-start gap-4 rounded-[20px] border border-[#EFEFEF] p-4 transition hover:border-[#FF914D] hover:bg-[#FFF9F5]"

              >

                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">

                  {isFile ? (

                    <FileDown className="h-5 w-5" />

                  ) : (

                    <ExternalLink className="h-5 w-5" />

                  )}

                </div>



                <div className="min-w-0 flex-1">

                  <h3 className="break-words text-sm font-black text-[#333333]">

                    {

                      resource.title

                    }

                  </h3>



                  {resource.description && (

                    <p className="mt-1 text-xs leading-relaxed text-[#757575]">

                      {

                        resource.description

                      }

                    </p>

                  )}



                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-bold text-[#8A8A8A]">

                    <span>

                      {isFile

                        ? "Download"

                        : "External link"}

                    </span>



                    {resource.file_name && (

                      <span>

                        ·{" "}

                        {

                          resource.file_name

                        }

                      </span>

                    )}



                    {sizeLabel && (

                      <span>

                        ·{" "}

                        {

                          sizeLabel

                        }

                      </span>

                    )}

                  </div>

                </div>



                <ExternalLink className="mt-1 h-4 w-4 flex-none text-[#A0A0A0] transition group-hover:text-[#FF914D]" />

              </a>

            );

          }

        )}

      </div>

    </section>

  );

}



function BackToCurriculum({

  cohortId,

}) {

  const destination =

    cohortId

      ? `/partner/curriculum?cohort=${encodeURIComponent(

          cohortId

        )}`

      : "/partner/curriculum";



  return (

    <Link

      to={destination}

      className="inline-flex items-center gap-2 text-sm font-black text-[#FF914D] transition hover:text-[#E9782F]"

    >

      <ArrowLeft className="h-4 w-4" />



      Back to curriculum

    </Link>

  );

}



function CatalogStat({

  label,

  value,

}) {

  return (

    <div className="min-w-[82px] rounded-2xl border border-[#EFEFEF] bg-[#FFFDFB] px-4 py-3 text-center">

      <p className="text-2xl font-black">

        {value}

      </p>



      <p className="mt-1 text-[10px] font-black uppercase tracking-wider text-[#757575]">

        {label}

      </p>

    </div>

  );

}



function LevelMetaPill({

  icon: Icon,

  label,

}) {

  return (

    <span className="inline-flex items-center gap-2 rounded-full border border-[#FFD9BF] bg-white px-4 py-2 text-xs font-black text-[#555555]">

      <Icon className="h-4 w-4 text-[#FF914D]" />



      {label}

    </span>

  );

}



function LoadingBlock({

  message,

}) {

  return (

    <div className="mt-6 rounded-[24px] border border-[#EFEFEF] bg-white p-8 text-center text-sm font-semibold text-[#757575] shadow-sm">

      {message}

    </div>

  );

}



function ErrorBlock({

  message,

  onRetry,

}) {

  return (

    <section className="mt-6 rounded-[24px] border border-red-200 bg-red-50 p-6">

      <p className="text-sm font-semibold text-red-700">

        {message}

      </p>



      <button

        type="button"

        onClick={onRetry}

        className="mt-4 inline-flex items-center rounded-full bg-[#FF914D] px-5 py-3 text-sm font-black text-white"

      >

        <RefreshCw className="mr-2 h-4 w-4" />



        Retry

      </button>

    </section>

  );

}



function EmptyBlock({

  message,

}) {

  return (

    <div className="mt-6 rounded-[24px] border border-dashed border-[#DDDDDD] bg-[#FAFAFA] p-8 text-center text-sm font-semibold text-[#757575]">

      {message}

    </div>

  );

}